// Service Worker Registration and Management
import { apiClient } from './api/client';

let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    if (import.meta.env.DEV) {
      console.warn('Service Workers or Push API not supported');
    }
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    serviceWorkerRegistration = registration;
    
    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    
    return registration;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Service Worker registration failed:', error);
    }
    return null;
  }
}

export function getServiceWorkerRegistration(): ServiceWorkerRegistration | null {
  return serviceWorkerRegistration;
}

export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  try {
    // Ensure service worker is registered
    let registration = serviceWorkerRegistration;
    if (!registration) {
      registration = await registerServiceWorker();
    }
    
    if (!registration) {
      console.warn('No service worker registration available');
      return null;
    }
    
    // Wait for service worker to be active
    await navigator.serviceWorker.ready;
    
    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Already subscribed - just ensure server knows about it
      await syncSubscriptionWithServer(subscription);
      return subscription;
    }
    
    // Get VAPID public key from server
    const { data } = await apiClient.get<{ publicKey: string }>('/push/public-key');
    const publicKey = data.publicKey;
    
    if (!publicKey) {
      console.warn('No VAPID public key configured on server');
      return null;
    }
    
    // Subscribe to push service
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    
    // Send subscription to server
    await syncSubscriptionWithServer(subscription);
    
    if (import.meta.env.DEV) {
      console.log('Push subscription successful');
    }
    
    return subscription;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to subscribe to push notifications:', error);
    }
    return null;
  }
}

async function syncSubscriptionWithServer(subscription: PushSubscription): Promise<void> {
  const subscriptionJson = subscription.toJSON();
  
  await apiClient.post('/push/subscribe', {
    endpoint: subscriptionJson.endpoint,
    keys: {
      p256dh: subscriptionJson.keys?.p256dh || '',
      auth: subscriptionJson.keys?.auth || '',
    }
  });
}

export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Unsubscribe from push service
      await subscription.unsubscribe();
      
      // Notify server
      await apiClient.post('/push/unsubscribe', { 
        endpoint: subscription.endpoint 
      });
      
      if (import.meta.env.DEV) {
        console.log('Push unsubscription successful');
      }
      return true;
    }
    return false;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to unsubscribe from push notifications:', error);
    }
    return false;
  }
}

export async function getPushSubscription(): Promise<PushSubscription | null> {
  try {
    if (!serviceWorkerRegistration) {
      return null;
    }
    return await serviceWorkerRegistration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Helper function to convert base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

// Check if user has granted notification permission
export function getNotificationPermission(): NotificationPermission {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return 'denied';
}

// Request notification permission
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if ('Notification' in window) {
    return await Notification.requestPermission();
  }
  return 'denied';
}

// Check if push is available and configured
export async function isPushAvailable(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }
  
  try {
    const { data } = await apiClient.get<{ publicKey: string }>('/push/public-key');
    return !!data.publicKey;
  } catch {
    return false;
  }
}
